import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sparkles, Eye, EyeOff, Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ResetPassword — page accessed from the email link sent by /api/auth/forgot-password.
 *
 * Reads the `token` from the URL query string, asks for a new password (twice),
 * and POSTs to /api/auth/reset-password. On success, redirects to /login.
 *
 * Public route (no auth needed) — token is the authentication.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // No token in URL → corrupted link, send back to login with message
  if (!token) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-gray-900 border border-red-500/30 rounded-2xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Lien invalide</h1>
          <p className="text-gray-400 mb-6">
            Ce lien de réinitialisation est incomplet ou corrompu.
          </p>
          <Link to="/login">
            <Button data-testid="reset-back-to-login-btn" className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white">
              Retour à la connexion
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(
        err.response?.data?.detail
          || 'La réinitialisation a échoué. Le lien est peut-être expiré.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-gray-900 border border-green-500/30 rounded-2xl p-8 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Mot de passe réinitialisé</h1>
          <p className="text-gray-400 mb-6">
            Vous allez être redirigé vers la connexion dans quelques secondes…
          </p>
          <Link to="/login">
            <Button data-testid="reset-success-login-btn" className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white">
              Se connecter maintenant
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center text-gray-400 hover:text-white mb-6 text-sm" data-testid="reset-back-link">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Retour à la connexion
        </Link>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
          <div className="flex items-center mb-6">
            <Sparkles className="w-6 h-6 text-cyan-400 mr-2" />
            <h1 className="text-xl font-bold text-white">Choisir un nouveau mot de passe</h1>
          </div>

          <p className="text-sm text-gray-400 mb-6">
            Pour des raisons de sécurité, choisissez un mot de passe d'au moins 8 caractères que vous n'utilisez pas ailleurs.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <Input
                  data-testid="reset-new-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="bg-gray-800 border-gray-700 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  data-testid="reset-toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Confirmer le mot de passe
              </label>
              <Input
                data-testid="reset-confirm-password-input"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            {error && (
              <div
                data-testid="reset-error-message"
                className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300"
                role="alert"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-btn"
              className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Réinitialisation…
                </>
              ) : (
                'Réinitialiser mon mot de passe'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
